export default class VideoTimeline extends HTMLElement {
  connectedCallback() {
    const url = this.getAttribute("video-url");
    if (!url) return;

    const bgHex = this.getAttribute("bg-color");
    this._bgColor = bgHex ? this._parseHex(bgHex) : null;
    this._clipBackground = this.getAttribute("clip-background") === "true";
    this._tolerance = parseInt(this.getAttribute("bg-tolerance") || "30", 10);
    this._feather = parseInt(this.getAttribute("bg-feather") || "20", 10);

    this.video = document.createElement("video");
    this.video.src = url;
    this.video.loop = true;
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.autoplay = true;
    this.video.addEventListener("canplay", () => this.video.play().catch(() => {}), { once: true });

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);

    if (this._bgColor || this._clipBackground) {
      this.video.style.display = "none";
      this.appendChild(this.video);

      this.canvas = document.createElement("canvas");
      this.canvas.style.cssText = "width:100%;height:100%;object-fit:contain;cursor:grab;display:block;";
      this.appendChild(this.canvas);
      this._ctx = this.canvas.getContext("2d", { willReadFrequently: true });
      this._rafId = requestAnimationFrame(this._renderFrame.bind(this));
      this._target = this.canvas;
    } else {
      this.video.style.cssText = "width:100%;height:100%;object-fit:contain;cursor:grab;display:block;";
      this.appendChild(this.video);
      this._target = this.video;
    }

    this._target.addEventListener("pointerdown", this._onPointerDown);
  }

  _parseHex(hex) {
    hex = hex.replace("#", "");
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  _renderFrame() {
    if (this.video.readyState >= 2) {
      const w = this.video.videoWidth;
      const h = this.video.videoHeight;
      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
      }
      this._ctx.drawImage(this.video, 0, 0, w, h);
      const frame = this._ctx.getImageData(0, 0, w, h);
      const data = frame.data;
      let keyColor = this._bgColor;
      if (this._clipBackground) {
        const p = (w - 1) * 4; // top-right pixel
        keyColor = { r: data[p], g: data[p + 1], b: data[p + 2] };
      }
      const { r: tr, g: tg, b: tb } = keyColor;
      const tol = this._tolerance;
      const feather = this._feather;
      for (let i = 0; i < data.length; i += 4) {
        const dr = data[i] - tr;
        const dg = data[i + 1] - tg;
        const db = data[i + 2] - tb;
        const dist = Math.sqrt(dr * dr + dg * dg + db * db);
        if (dist <= tol) {
          data[i + 3] = 0;
        } else if (dist < tol + feather) {
          data[i + 3] = Math.round(data[i + 3] * (dist - tol) / feather);
        }
      }
      this._ctx.putImageData(frame, 0, 0);
    }
    this._rafId = requestAnimationFrame(this._renderFrame.bind(this));
  }

  _onPointerDown(e) {
    e.preventDefault();
    this._dragging = true;
    this._startX = e.clientX;
    this._startTime = this.video.currentTime;
    this.video.pause();
    this._target.style.cursor = "grabbing";

    window.addEventListener("pointermove", this._onPointerMove);
    window.addEventListener("pointerup", this._onPointerUp);
  }

  _onPointerMove(e) {
    if (!this._dragging) return;
    const dx = e.clientX - this._startX;
    const sensitivity = (this.video.duration || 10) / this.offsetWidth;
    let newTime = this._startTime + dx * sensitivity;
    const duration = this.video.duration || 10;
    newTime = ((newTime % duration) + duration) % duration;
    this.video.currentTime = newTime;
  }

  _onPointerUp() {
    this._dragging = false;
    this.video.play();
    this._target.style.cursor = "grab";
    window.removeEventListener("pointermove", this._onPointerMove);
    window.removeEventListener("pointerup", this._onPointerUp);
  }

  disconnectedCallback() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    window.removeEventListener("pointermove", this._onPointerMove);
    window.removeEventListener("pointerup", this._onPointerUp);
  }
}
